
import React, { useEffect, useRef } from 'react';
import { createRoot } from 'react-dom/client';
import { PhotoProvider, PhotoView } from 'react-photo-view';
import 'react-photo-view/dist/react-photo-view.css';

// 图片预览器组件
interface ImageViewerProps {
  src: string;
  onClose: () => void;
}

const ImageViewer: React.FC<ImageViewerProps> = ({ src, onClose }) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;


    // 监听键盘事件，ESC键关闭
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 z-[9999] bg-black bg-opacity-90 flex items-center justify-center"
      onClick={onClose}
    >
      {/* 操作按钮组 */}
      <div className="absolute top-4 right-4 z-[10000] flex gap-2">
        {/* 下载按钮 */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            const link = document.createElement('a');
            link.href = src;
            link.download = src.split('/').pop() || 'image';
            link.target = '_blank';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }}
          className="w-10 h-10 bg-black bg-opacity-50 hover:bg-opacity-70 text-white rounded-full flex items-center justify-center  duration-200 "
          style={{ backdropFilter: 'blur(4px)' }}
          title="下载图片"
        >
          <i className="text-lg">⬇</i>
        </button>

        {/* 外部打开按钮 */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            window.open(src, '_blank');
          }}
          className="w-10 h-10 bg-black bg-opacity-50 hover:bg-opacity-70 text-white rounded-full flex items-center justify-center transition-all duration-200 backdrop-blur-sm"
          style={{ backdropFilter: 'blur(4px)' }}
          title="在外部打开"
        >
          <i className="text-lg">🔗</i>
        </button>

        {/* 关闭按钮 */}
        <button
          onClick={onClose}
          className="w-10 h-10 bg-black bg-opacity-50 hover:bg-opacity-70 text-white rounded-full flex items-center justify-center transition-all duration-200 backdrop-blur-sm"
          style={{ backdropFilter: 'blur(4px)' }}
          title="关闭"
        >
          <i className="text-xl">×</i>
        </button>
      </div>

      <PhotoProvider>
        <PhotoView src={src}>
          <img
            src={src}
            alt="Preview"
            className="max-w-full max-h-full object-contain"
            draggable={false}
          />
        </PhotoView>
      </PhotoProvider>
    </div>
  );
};

/**
 * 全局显示图片预览器
 * @param src 图片地址
 * @param options 可选配置
 */
export const showImage = (src: string, options?: { onClose?: () => void }) => {
  // 创建容器元素
  const container = document.createElement('div');
  container.className = 'image-viewer-container';
  document.body.appendChild(container);

  // 创建 React 根节点
  const root = createRoot(container);

  const handleClose = () => {
    // 卸载组件并移除容器
    root.unmount();
    if (document.body.contains(container)) {
      document.body.removeChild(container);
    }
    // 执行自定义关闭回调
    options?.onClose?.();
  };

  // 渲染图片预览组件
  root.render(<ImageViewer src={src} onClose={handleClose} />);

  // 返回关闭函数，允许外部主动关闭
  return handleClose;
};

